import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { 
  Plus,
  Trash2,
  Package,
  Edit2,
  Check,
  X,
  ShieldOff,
  Search,
  ChevronDown,
  UserPlus,
  Phone,
  Mail,
  MapPin,
  Building2,
  User,
  CreditCard,
  CalendarIcon
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateInvoice() {
  const navigate = useNavigate();
  const { businessSlug } = useParams();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [laborPrices, setLaborPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Customer search state
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef(null);
  
  // Labor description suggestions
  const [showLaborSuggestions, setShowLaborSuggestions] = useState(false);
  const laborInputRef = useRef(null);
  
  const API = `${BASE_API}/b/${businessSlug}`;
  const basePath = `/${businessSlug}`;
  
  const [formData, setFormData] = useState({
    customer_id: "",
    customer_name: "", // For walk-in customers without saving
    customer_phone: "", // For walk-in customers
    vehicle_info: "",
    truck_number: "",
    items: [],
    tax_rate: 0.075,
    tax_exempt: false,
    card_fee_enabled: false, // Default OFF - user can toggle on when needed
    notes: "",
    due_date: ""
  });

  // Tax settings from backend
  const [taxSettings, setTaxSettings] = useState({
    tax_labor: false, // Default: don't tax labor
    default_tax_rate: 7.5
  });

  // For editing items inline
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // QuickBooks-style single row input
  const [newItemDescription, setNewItemDescription] = useState("");
  const [newItemType, setNewItemType] = useState("part");
  const [newItemQty, setNewItemQty] = useState(1);
  const [newItemRate, setNewItemRate] = useState("");
  const [selectedInventoryId, setSelectedInventoryId] = useState("");
  const [selectedPartCost, setSelectedPartCost] = useState(0);
  // Mirrors the inventory item's `track_profit` so we pre-fill the line's
  // from_inventory toggle correctly when the part comes out of inventory.
  const [selectedPartTrackProfit, setSelectedPartTrackProfit] = useState(true);

  const [partSearch, setPartSearch] = useState("");
  const [showPartDropdown, setShowPartDropdown] = useState(false);
  // Per-business line-item type labels: [{ key, label, ... }, ...]
  // The Select stores the `key` (e.g. "part"); only the displayed label changes.
  const [lineItemTypes, setLineItemTypes] = useState([
    { key: "part", label: "Part", track_inventory: true, is_default: true },
    { key: "labor", label: "Labor", track_inventory: false, is_default: false },
  ]);

  /**
   * Resolve whether a given line-item `type` key represents an inventory-tracked
   * category for *this* business. Custom categories added in Settings
   * (e.g. "tire", "material") flow through here so inventory deduction,
   * cost-of-goods accounting, and tax-by-default behave like the legacy "part"
   * key — and labor-style categories behave like the legacy "labor" key.
   *
   * Falls back to the literal "part"/"labor" semantics when lineItemTypes hasn't
   * loaded yet (first render before /line-item-types resolves) so legacy
   * behaviour stays correct.
   */
  const tracksInventory = (key) => {
    const row = lineItemTypes.find((t) => t.key === key);
    if (row) return !!row.track_inventory;
    return key === "part";
  };

  // Demo hook: expose setters so the DemoPlayer can guarantee state changes
  // on shadcn/Radix <Select> elements (which can be flaky to drive via fake
  // pointer events alone). Cleaned up on unmount. Harmless in production.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__demoSetItemType = (v) => setNewItemType(v);
    return () => { try { delete window.__demoSetItemType; } catch (e) {} };
  }, []);
  
  // Quick add customer modal
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    company_name: "",
    email: "",
    phone: "",
    address: "",
    driver_number: ""
  });
  const [addingCustomer, setAddingCustomer] = useState(false);

  useEffect(() => {
    if (businessSlug) {
      fetchData();
    }
  }, [businessSlug]);

  // Auto-set tax exempt based on customer
  useEffect(() => {
    if (formData.customer_id) {
      const customer = customers.find(c => c.id === formData.customer_id);
      if (customer) {
        setFormData(prev => ({ ...prev, tax_exempt: customer.tax_exempt || false }));
      }
    }
  }, [formData.customer_id, customers]);

  // Close customer dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
      if (laborInputRef.current && !laborInputRef.current.contains(event.target)) {
        setShowLaborSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, inventoryRes, settingsRes, laborPricesRes, lineTypesRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/inventory`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/labor-prices`),
        axios.get(`${API}/line-item-types`),
      ]);
      setCustomers(customersRes.data);
      setInventory(inventoryRes.data);
      setSettings(settingsRes.data);
      setLaborPrices(laborPricesRes.data);
      // Per-business line-item type labels. The Select keeps storing the
      // underlying `key` ("part"/"labor"); only the displayed label changes.
      // This means existing invoices, dashboard math, and tax reports keep
      // working unchanged after a label rename.
      setLineItemTypes(lineTypesRes.data.line_item_types || []);
      // Set tax settings
      if (settingsRes.data.tax_settings) {
        setTaxSettings(settingsRes.data.tax_settings);
        // Sync new-invoice default rate to the business's configured rate
        // (stored as percent, e.g. 7.5) so if the customer changes it in
        // Settings, new invoices pick it up automatically.
        const defaultPct = settingsRes.data.tax_settings.default_tax_rate;
        if (typeof defaultPct === 'number' && defaultPct >= 0) {
          setFormData(prev => ({ ...prev, tax_rate: defaultPct / 100 }));
        }
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fill labor price when description matches
  const handleLaborDescriptionChange = (value) => {
    setNewItemDescription(value);
    
    // Check if this description matches any saved labor price
    const match = laborPrices.find(lp => 
      lp.description.toLowerCase() === value.toLowerCase()
    );
    
    if (match) {
      setNewItemRate(match.price.toString());
    }
    
    // Show suggestions if typing
    setShowLaborSuggestions(value.length > 0 && newItemType === 'labor');
  };

  // Select a labor suggestion
  const selectLaborSuggestion = (laborPrice) => {
    setNewItemDescription(laborPrice.description);
    setNewItemRate(laborPrice.price.toString());
    setShowLaborSuggestions(false);
  };

  // Filter labor suggestions based on input
  const filteredLaborSuggestions = laborPrices.filter(lp =>
    lp.description.toLowerCase().includes(newItemDescription.toLowerCase())
  );

  // Quick add customer from invoice page
  const handleQuickAddCustomer = async () => {
    if (!newCustomer.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    
    setAddingCustomer(true);
    try {
      const response = await axios.post(`${API}/customers`, newCustomer);
      const addedCustomer = response.data;
      
      // Add to customers list
      setCustomers(prev => [...prev, addedCustomer]);
      
      // Auto-select the new customer
      setFormData(prev => ({ ...prev, customer_id: addedCustomer.id }));
      setCustomerSearch(addedCustomer.name);
      
      // Reset form and close modal
      setNewCustomer({
        name: "",
        company_name: "",
        email: "",
        phone: "",
        address: "",
        driver_number: ""
      });
      setShowAddCustomer(false);
      setShowCustomerDropdown(false);
      
      toast.success(`Customer "${addedCustomer.name}" added and selected!`);
    } catch (error) {
      toast.error("Failed to add customer");
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id && !formData.customer_name) {
      toast.error("Please enter a customer name or select a customer");
      return;
    }
    
    if (formData.items.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    setSubmitting(true);
    try {
      // Save labor prices for future use
      const laborItems = formData.items.filter(item => item.type === 'labor');
      for (const item of laborItems) {
        try {
          await axios.post(`${API}/labor-prices?description=${encodeURIComponent(item.description)}&price=${item.unit_price}`);
        } catch (err) {
          // Silently continue if saving labor price fails
          console.log("Could not save labor price:", err);
        }
      }
      
      const response = await axios.post(`${API}/invoices`, formData);
      toast.success("Invoice created successfully");
      navigate(`${basePath}/invoices/${response.data.id}`);
    } catch (error) {
      toast.error("Failed to create invoice");
    } finally {
      setSubmitting(false);
    }
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const startEditItem = (index) => {
    setEditingIndex(index);
    setEditingItem({ ...formData.items[index] });
  };

  const saveEditItem = () => {
    if (!editingItem.description || editingItem.unit_price <= 0) {
      toast.error("Please fill in all fields");
      return;
    }
    const newItems = [...formData.items];
    newItems[editingIndex] = editingItem;
    setFormData({ ...formData, items: newItems });
    setEditingIndex(null);
    setEditingItem(null);
  };

  const cancelEditItem = () => {
    setEditingIndex(null);
    setEditingItem(null);
  };

  const filteredParts = inventory.filter(item => 
    item.name.toLowerCase().includes(newItemDescription.toLowerCase()) ||
    item.part_number.toLowerCase().includes(newItemDescription.toLowerCase())
  );

  const addNewItem = () => {
    const rate = parseFloat(newItemRate) || 0;
    // For inventory items, description is required. For service items it's optional.
    if (tracksInventory(newItemType) && !newItemDescription) {
      toast.error("Please enter a description for this item");
      return;
    }
    if (rate <= 0) {
      toast.error("Please enter a valid rate");
      return;
    }

    // Inventory-tracked items are always taxable (unless the customer is
    // tax-exempt). Service items follow the per-business `tax_labor` setting.
    const isInventoryItem = tracksInventory(newItemType);
    const isItemTaxable = formData.tax_exempt
      ? false
      : (isInventoryItem ? true : !!taxSettings.tax_labor);

    const item = {
      type: newItemType,
      description: newItemDescription || "",
      quantity: newItemQty,
      unit_price: rate,
      taxable: isItemTaxable,
      inventory_id: selectedInventoryId,
      cost: isInventoryItem ? selectedPartCost : 0,
      // Inventory items: respect the picked part's track_profit flag.
      // Service items: never count cost-of-goods.
      from_inventory: isInventoryItem ? selectedPartTrackProfit : false,
    };

    setFormData({
      ...formData,
      items: [...formData.items, item]
    });

    // Reset inputs - keep type as is, clear everything else
    setNewItemDescription("");
    setNewItemQty(1);
    setNewItemRate("");
    setSelectedInventoryId("");
    setSelectedPartCost(0);
    setSelectedPartTrackProfit(true);
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const calculateTax = () => {
    if (formData.tax_exempt) return 0;
    const taxableSubtotal = formData.items
      .filter(item => item.taxable)
      .reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    return taxableSubtotal * formData.tax_rate;
  };

  const calculateCardFee = () => {
    if (!formData.card_fee_enabled) return 0;
    return (calculateSubtotal() + calculateTax()) * 0.03;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() + calculateCardFee();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const selectedCustomer = customers.find(c => c.id === formData.customer_id);
  const taxRates = settings?.tax_rates || [0, 6, 7.5, 8, 8.25, 10];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="create-invoice-loading">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="create-invoice-page">
      {/* Header */}
      <div>
        <h1 className={`text-2xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
          New Invoice
        </h1>
        <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Create a new invoice for a customer
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Selection */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <CardTitle className={isDark ? 'text-white' : 'text-slate-800'}>
                  Customer
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Customer</Label>
                  <div className="relative" ref={customerDropdownRef}>
                    <div className="relative">
                      <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                      <Input
                        type="text"
                        placeholder="Search or type customer name..."
                        value={customerSearch}
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          setShowCustomerDropdown(true);
                          // Also set as walk-in customer name
                          if (!formData.customer_id) {
                            setFormData(prev => ({ ...prev, customer_name: e.target.value }));
                          }
                        }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'}`}
                        data-testid="customer-search-input"
                      />
                    </div>
                    
                    {/* Customer dropdown */}
                    {showCustomerDropdown && customerSearch && (
                      <div className={`absolute z-50 w-full mt-1 max-h-60 overflow-auto rounded-md border shadow-lg ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        {/* Use as walk-in option */}
                        <div
                          className={`px-4 py-3 cursor-pointer border-b ${isDark ? 'border-slate-700 hover:bg-green-900/30' : 'border-slate-200 hover:bg-green-50'}`}
                          onClick={() => {
                            setFormData({ ...formData, customer_id: "", customer_name: customerSearch });
                            setShowCustomerDropdown(false);
                          }}
                          data-testid="use-as-walkin-btn"
                        >
                          <div className="flex items-center gap-2 text-green-600 font-medium">
                            <User className="w-4 h-4" />
                            <span>Use "{customerSearch}" as walk-in customer</span>
                          </div>
                        </div>
                        
                        {customers
                          .filter(c => 
                            c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
                            (c.company_name && c.company_name.toLowerCase().includes(customerSearch.toLowerCase())) ||
                            (c.phone && c.phone.includes(customerSearch)) ||
                            (c.driver_number && c.driver_number.toLowerCase().includes(customerSearch.toLowerCase()))
                          )
                          .map((customer) => (
                            <div
                              key={customer.id}
                              data-testid="customer-match-row"
                              className={`px-4 py-3 cursor-pointer border-b last:border-b-0 ${
                                formData.customer_id === customer.id
                                  ? isDark ? 'bg-blue-900/30' : 'bg-blue-50'
                                  : isDark ? 'hover:bg-slate-700' : 'hover:bg-slate-50'
                              } ${isDark ? 'border-slate-700' : 'border-slate-100'}`}
                              onClick={() => {
                                setFormData({ ...formData, customer_id: customer.id, customer_name: "" });
                                setCustomerSearch(customer.name);
                                setShowCustomerDropdown(false);
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                                    {customer.name}
                                  </p>
                                  {customer.company_name && (
                                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                                      {customer.company_name}
                                    </p>
                                  )}
                                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                                    {customer.phone} {customer.driver_number && `• Driver #${customer.driver_number}`}
                                  </p>
                                </div>
                                {customer.tax_exempt && (
                                  <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded">
                                    Tax Exempt
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        {/* Quick Add Customer Button */}
                        <div 
                          className={`px-4 py-3 cursor-pointer border-t ${isDark ? 'border-slate-700 hover:bg-slate-700' : 'border-slate-200 hover:bg-blue-50'}`}
                          onClick={() => {
                            setShowAddCustomer(true);
                            setShowCustomerDropdown(false);
                            // Pre-fill the name if user typed something
                            if (customerSearch.trim() && !customers.some(c => c.name.toLowerCase() === customerSearch.toLowerCase())) {
                              setNewCustomer(prev => ({ ...prev, name: customerSearch.trim() }));
                            }
                          }}
                          data-testid="quick-add-customer-btn"
                        >
                          <div className="flex items-center gap-2 text-blue-600 font-medium">
                            <UserPlus className="w-4 h-4" />
                            <span>Save as New Customer</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Show selected customer OR walk-in customer */}
                {selectedCustomer ? (
                  <div className={`p-3 rounded border ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{selectedCustomer.name}</p>
                        {selectedCustomer.company_name && (
                          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{selectedCustomer.company_name}</p>
                        )}
                        {selectedCustomer.phone && <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{selectedCustomer.phone}</p>}
                        {selectedCustomer.driver_number && (
                          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Driver #: {selectedCustomer.driver_number}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedCustomer.tax_exempt && (
                          <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded">
                            <ShieldOff className="w-3 h-3" />
                            Tax Exempt
                          </span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, customer_id: "", customer_name: "" }));
                            setCustomerSearch("");
                          }}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : formData.customer_name ? (
                  <div className={`p-3 rounded border ${isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`font-medium ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                          <User className="w-4 h-4 inline mr-1" />
                          Walk-in: {formData.customer_name}
                        </p>
                        <p className={`text-xs ${isDark ? 'text-green-500' : 'text-green-600'}`}>
                          Customer not saved to database
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, customer_name: "" }));
                          setCustomerSearch("");
                        }}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : null}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Vehicle Info</Label>
                    <Input
                      placeholder="e.g., 2020 Volvo VNL 860"
                      value={formData.vehicle_info}
                      onChange={(e) => setFormData({ ...formData, vehicle_info: e.target.value })}
                      className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                      data-testid="vehicle-info-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Truck #</Label>
                    <Input
                      placeholder="Truck number"
                      value={formData.truck_number}
                      onChange={(e) => setFormData({ ...formData, truck_number: e.target.value })}
                      className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                      data-testid="truck-number-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Line Items */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className={`border-b pb-4 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between">
                  <CardTitle className={isDark ? 'text-white' : 'text-slate-800'}>
                    Items
                  </CardTitle>
                  {formData.tax_exempt && (
                    <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 font-medium">
                      TAX EXEMPT
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Table Header */}
                <div className={`grid grid-cols-12 gap-2 px-4 py-3 text-xs uppercase tracking-wider ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-slate-50 text-slate-500'}`}>
                  <div className="col-span-5">Description</div>
                  <div className="col-span-2">Type</div>
                  <div className="col-span-1 text-center">Qty</div>
                  <div className="col-span-2 text-right">Rate</div>
                  <div className="col-span-2 text-right">Amount</div>
                </div>

                {/* Existing Items */}
                {formData.items.map((item, index) => (
                  <div key={index} className={`grid grid-cols-12 gap-2 px-4 py-3 items-center ${isDark ? 'border-b border-slate-700 hover:bg-slate-700/50' : 'border-b border-slate-200 hover:bg-slate-50'}`}>
                    {editingIndex === index ? (
                      // Editing mode
                      <>
                        <div className="col-span-5">
                          <Input
                            value={editingItem.description}
                            onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                            className={`h-8 ${isDark ? 'bg-slate-600 border-slate-500' : 'bg-white border-slate-200'}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Select value={editingItem.type} onValueChange={(v) => setEditingItem({ ...editingItem, type: v })}>
                            <SelectTrigger className={`h-8 ${isDark ? 'bg-slate-600 border-slate-500' : 'bg-white border-slate-200'}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                              {lineItemTypes.map((t) => (
                                <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1">
                          <Input
                            type="number"
                            min="0.5"
                            step="0.5"
                            value={editingItem.quantity}
                            onChange={(e) => setEditingItem({ ...editingItem, quantity: parseFloat(e.target.value) || 1 })}
                            className={`h-8 text-center ${isDark ? 'bg-slate-600 border-slate-500' : 'bg-white border-slate-200'}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editingItem.unit_price}
                            onChange={(e) => setEditingItem({ ...editingItem, unit_price: parseFloat(e.target.value) || 0 })}
                            className={`h-8 text-right ${isDark ? 'bg-slate-600 border-slate-500' : 'bg-white border-slate-200'}`}
                          />
                        </div>
                        <div className="col-span-2 flex justify-end gap-1">
                          <Button type="button" size="icon" variant="ghost" onClick={saveEditItem} className="h-7 w-7 text-green-600 hover:bg-green-50">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button type="button" size="icon" variant="ghost" onClick={cancelEditItem} className="h-7 w-7 text-red-600 hover:bg-red-50">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      // View mode
                      <>
                        <div className={`col-span-4 ${isDark ? 'text-white' : 'text-slate-800'}`}>{item.description}</div>
                        <div className="col-span-2 flex items-center gap-1">
                          <span className={`text-xs px-2 py-1 rounded ${tracksInventory(item.type) ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {(lineItemTypes.find(t => t.key === item.type)?.label || item.type).toUpperCase()}
                          </span>
                          {/* Inventory toggle removed — whether a line counts
                              toward inventory is now controlled entirely by
                              the per-category "Track Inventory" setting on
                              the Line-Item Categories card in Settings.
                              No per-line override needed (and showing one
                              per row was confusing operators). */}
                        </div>
                        <div className={`col-span-1 text-center ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{item.quantity}</div>
                        <div className={`col-span-2 text-right ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{formatCurrency(item.unit_price)}</div>
                        <div className="col-span-3 text-right flex items-center justify-end gap-1">
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{formatCurrency(item.quantity * item.unit_price)}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => startEditItem(index)}
                            className="h-6 w-6 text-blue-600 hover:bg-blue-50"
                            data-testid={`edit-item-${index}`}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(index)}
                            className="h-6 w-6 text-red-500 hover:bg-red-50"
                            data-testid={`remove-item-${index}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {/* Add New Item Row */}
                <div className={`grid grid-cols-12 gap-2 px-4 py-3 items-end ${isDark ? 'bg-slate-700/30' : 'bg-slate-50/50'}`}>
                  <div className="col-span-5 relative" ref={laborInputRef}>
                    {(() => {
                      // Use the business's chosen label for the active type
                      const activeLabel = (lineItemTypes.find(t => t.key === newItemType)?.label) || newItemType;
                      const placeholder = newItemType === "labor"
                        ? `Enter ${activeLabel.toLowerCase()} description...`
                        : `Enter ${activeLabel.toLowerCase()} or search inventory...`;
                      return (
                    <Input
                      placeholder={placeholder}
                      value={newItemDescription}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (newItemType === "labor") {
                          handleLaborDescriptionChange(value);
                        } else {
                          setNewItemDescription(value);
                          // Only show dropdown for parts, not labor
                          setShowPartDropdown(true);
                        }
                      }}
                      onFocus={() => {
                        if (newItemType === "part") {
                          setShowPartDropdown(true);
                        } else if (newItemType === "labor" && newItemDescription.length > 0) {
                          setShowLaborSuggestions(true);
                        }
                      }}
                      onBlur={() => setTimeout(() => setShowPartDropdown(false), 200)}
                      className={`h-9 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}
                      data-testid="new-item-description"
                    />
                      );
                    })()}
                    {/* Inventory Dropdown — show whenever the active type tracks inventory */}
                    {showPartDropdown && tracksInventory(newItemType) && newItemDescription && filteredParts.length > 0 && (
                      <div className={`absolute z-50 mt-1 w-full rounded border shadow-xl max-h-48 overflow-auto ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        {filteredParts.slice(0, 8).map((part) => (
                          <div
                            key={part.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setNewItemDescription(part.name);
                              setNewItemRate(part.price);
                              setNewItemType("part");
                              setSelectedInventoryId(part.id);
                              setSelectedPartCost(part.cost || 0);  // Track cost for profit calculation
                              // Respect the inventory item's `track_profit` flag.
                              // If the part is marked "don't track profit" (price
                              // varies per customer), we'll set the upcoming line's
                              // from_inventory to FALSE so it's pass-through.
                              setSelectedPartTrackProfit(part.track_profit !== false);
                              setShowPartDropdown(false);
                            }}
                            className={`p-2 cursor-pointer ${isDark ? 'hover:bg-slate-700 border-b border-slate-700' : 'hover:bg-slate-50 border-b border-slate-100'}`}
                          >
                            <div className="flex justify-between text-sm">
                              <span className={isDark ? 'text-white' : 'text-slate-800'}>{part.name}</span>
                              <span className="font-medium text-blue-600">{formatCurrency(part.price)}</span>
                            </div>
                            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{part.part_number} • {part.quantity} in stock</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Labor Suggestions Dropdown */}
                    {showLaborSuggestions && newItemType === "labor" && filteredLaborSuggestions.length > 0 && (
                      <div className={`absolute z-50 mt-1 w-full rounded border shadow-xl max-h-48 overflow-auto ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        {filteredLaborSuggestions.slice(0, 8).map((lp) => (
                          <div
                            key={lp.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              selectLaborSuggestion(lp);
                            }}
                            className={`p-2 cursor-pointer ${isDark ? 'hover:bg-slate-700 border-b border-slate-700' : 'hover:bg-slate-50 border-b border-slate-100'}`}
                          >
                            <div className="flex justify-between text-sm">
                              <span className={isDark ? 'text-white' : 'text-slate-800'}>{lp.description}</span>
                              <span className="font-medium text-purple-600">{formatCurrency(lp.price)}</span>
                            </div>
                            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Previously used labor rate</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Select 
                      value={newItemType} 
                      onValueChange={(value) => {
                        setNewItemType(value);
                        // Clear fields when switching to labor
                        if (value === "labor") {
                          setSelectedInventoryId("");
                          setShowPartDropdown(false);
                        }
                      }}
                    >
                      <SelectTrigger className={`h-9 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`} data-testid="new-item-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                        {lineItemTypes.map((t) => (
                          <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      min="0.5"
                      step="0.5"
                      value={newItemQty}
                      onChange={(e) => setNewItemQty(parseFloat(e.target.value) || 1)}
                      className={`h-9 text-center ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}
                      data-testid="new-item-qty"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={newItemRate}
                      onChange={(e) => setNewItemRate(e.target.value)}
                      className={`h-9 text-right ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}
                      data-testid="new-item-rate"
                    />
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <Button
                      type="button"
                      onClick={addNewItem}
                      disabled={!newItemRate || parseFloat(newItemRate) <= 0 || (newItemType === "part" && !newItemDescription)}
                      className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4"
                      data-testid="add-item-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>

                {/* Empty State */}
                {formData.items.length === 0 && (
                  <div className={`text-center py-8 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No items added yet. Start typing above to add items.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
              <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <CardTitle className={isDark ? 'text-white' : 'text-slate-800'}>Notes</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <Textarea
                  placeholder="Add any notes for this invoice..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className={`min-h-[100px] ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                  data-testid="invoice-notes"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Summary */}
          <div className="space-y-6">
            <Card className={`sticky top-4 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
              <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                <CardTitle className={isDark ? 'text-white' : 'text-slate-800'}>Summary</CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {/* Tax Exempt Toggle */}
                <div className={`p-3 rounded-lg border ${formData.tax_exempt ? 'bg-amber-50 border-amber-300' : isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldOff className={`w-4 h-4 ${formData.tax_exempt ? 'text-amber-600' : isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                      <span className={`font-medium ${formData.tax_exempt ? 'text-amber-700' : isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        Tax Exempt
                      </span>
                    </div>
                    <Switch
                      checked={formData.tax_exempt}
                      onCheckedChange={(checked) => {
                        const defaultRate = (taxSettings.default_tax_rate || 7.5) / 100;
                        // Update items' taxable status when toggling tax exempt
                        // When turning OFF tax exempt, mark parts as taxable, and labor based on tax_labor setting
                        const updatedItems = formData.items.map(item => ({
                          ...item,
                          taxable: checked ? false : (tracksInventory(item.type) ? true : !!taxSettings.tax_labor)
                        }));
                        // Tax exempt toggle
                        setFormData(prev => ({ 
                          ...prev, 
                          tax_exempt: checked, 
                          tax_rate: checked ? 0 : defaultRate,
                          items: updatedItems
                        }));
                      }}
                      data-testid="tax-exempt-summary-toggle"
                    />
                  </div>
                  {formData.tax_exempt && (
                    <p className="text-xs text-amber-600 mt-2">No tax will be applied to this invoice</p>
                  )}
                </div>

                {/* Tax Rate - Only show if not tax exempt */}
                {!formData.tax_exempt && (
                  <div className="space-y-2">
                    <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Tax Rate</Label>
                    <Select 
                      value={formData.tax_rate.toString()} 
                      onValueChange={(value) => setFormData({ ...formData, tax_rate: parseFloat(value) })}
                    >
                      <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'} data-testid="tax-rate-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                        {taxRates.map((rate) => (
                          <SelectItem key={rate} value={(rate / 100).toString()}>
                            {rate === 0 ? "No Tax (0%)" : `${rate}%`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          isDark ? 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600' : 'bg-slate-50 border-slate-200 hover:bg-slate-100',
                          !formData.due_date && (isDark ? 'text-slate-400' : 'text-slate-500')
                        )}
                        data-testid="due-date-input"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.due_date
                          ? format(parseISO(formData.due_date), "PPP")
                          : "Pick a due date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className={`w-auto p-0 ${isDark ? 'bg-slate-800 border-slate-700' : ''}`}
                      align="start"
                      data-testid="due-date-popover"
                    >
                      <Calendar
                        mode="single"
                        selected={formData.due_date ? parseISO(formData.due_date) : undefined}
                        onSelect={(d) => setFormData({ ...formData, due_date: d ? format(d, "yyyy-MM-dd") : "" })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Card Fee Toggle */}
                <div className={`p-3 rounded-lg border ${formData.card_fee_enabled ? 'bg-blue-50 border-blue-300' : isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className={`w-4 h-4 ${formData.card_fee_enabled ? 'text-blue-600' : isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                      <span className={`font-medium ${formData.card_fee_enabled ? 'text-blue-700' : isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                        Card Fee (3%)
                      </span>
                    </div>
                    <Switch
                      checked={formData.card_fee_enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, card_fee_enabled: checked })}
                      data-testid="card-fee-toggle"
                    />
                  </div>
                  {formData.card_fee_enabled && (
                    <p className="text-xs text-blue-600 mt-2">
                      3% processing fee will be added if customer pays with card
                    </p>
                  )}
                </div>

                <Separator className={isDark ? 'bg-slate-700' : 'bg-slate-200'} />

                <div className="space-y-2">
                  <div className={`flex justify-between ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    <span>Subtotal</span>
                    <span>{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  <div className={`flex justify-between ${(formData.tax_exempt || formData.tax_rate === 0) ? 'text-amber-600' : isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                    <span>Tax {(formData.tax_exempt || formData.tax_rate === 0) ? '' : `(${(formData.tax_rate * 100).toFixed(1).replace(/\.0$/, '')}%)`}</span>
                    <span>{(formData.tax_exempt || formData.tax_rate === 0) ? 'TAX EXEMPT' : formatCurrency(calculateTax())}</span>
                  </div>
                  {formData.card_fee_enabled && (
                    <div className={`flex justify-between text-blue-600`}>
                      <span>Card Fee (3%)</span>
                      <span>{formatCurrency(calculateCardFee())}</span>
                    </div>
                  )}
                  <Separator className={isDark ? 'bg-slate-700' : 'bg-slate-200'} />
                  <div className="flex justify-between text-xl font-bold">
                    <span className={isDark ? 'text-white' : 'text-slate-800'}>Total</span>
                    <span className="text-blue-600">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting || (!formData.customer_id && !formData.customer_name) || formData.items.length === 0}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold mt-4"
                  data-testid="create-invoice-submit"
                >
                  {submitting ? "Creating..." : "Create Invoice"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>

      {/* Quick Add Customer Dialog */}
      <Dialog open={showAddCustomer} onOpenChange={setShowAddCustomer}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
              <UserPlus className="w-5 h-5 text-blue-600" />
              Add New Customer
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-slate-400' : 'text-slate-500'}>
              Create a new customer and add them to this invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Customer Name *
                </span>
              </Label>
              <Input
                placeholder="John Doe"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-slate-50 border-slate-200'}
                data-testid="new-customer-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                <span className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Company Name
                </span>
              </Label>
              <Input
                placeholder="ABC Trucking LLC"
                value={newCustomer.company_name}
                onChange={(e) => setNewCustomer({ ...newCustomer, company_name: e.target.value })}
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-slate-50 border-slate-200'}
                data-testid="new-customer-company-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                  <span className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone
                  </span>
                </Label>
                <Input
                  placeholder="(555) 123-4567"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-slate-50 border-slate-200'}
                  data-testid="new-customer-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </span>
                </Label>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-slate-50 border-slate-200'}
                  data-testid="new-customer-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </span>
              </Label>
              <Input
                placeholder="123 Main St, City, State"
                value={newCustomer.address}
                onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-slate-50 border-slate-200'}
                data-testid="new-customer-address-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddCustomer(false);
                setNewCustomer({
                  name: "",
                  company_name: "",
                  email: "",
                  phone: "",
                  address: "",
                  driver_number: ""
                });
              }}
              className={isDark ? 'border-slate-600' : 'border-slate-200'}
              data-testid="cancel-add-customer-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleQuickAddCustomer}
              disabled={addingCustomer || !newCustomer.name.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="submit-add-customer-btn"
            >
              {addingCustomer ? "Adding..." : "Add Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
